import type { BakedBlockPlacement } from '@tsagko/label-contracts'
import type { Box } from './split'

export type { Box }

/**
 * Projects a baked-block placement onto a host box: the returned box is
 * where the baked instance's `expansion` should be laid out.
 *
 * Layout rule (per spec §2):
 *   dx = hAlign === 'left'   ? 0
 *      : hAlign === 'right'  ? host.w - placement.widthMm
 *      :                       (host.w - placement.widthMm) / 2
 *   dy = same with vAlign over the height axis
 *
 * Pure: does not mutate `host` or `placement`. Caller passes a host whose
 * width/height are at least the placement's; this helper does NOT enforce
 * that invariant — the fit check (lint and drop) is the caller's
 * responsibility. When placement exceeds host, dx/dy go negative and the
 * lint rule `PLACEMENT_OVERFLOW` fires (Task 9).
 */
export function projectPlacement(host: Box, placement: BakedBlockPlacement): Box {
  const dx =
    placement.hAlign === 'left'
      ? 0
      : placement.hAlign === 'right'
        ? host.w - placement.widthMm
        : (host.w - placement.widthMm) / 2
  const dy =
    placement.vAlign === 'top'
      ? 0
      : placement.vAlign === 'bottom'
        ? host.h - placement.heightMm
        : (host.h - placement.heightMm) / 2
  return {
    x: host.x + dx,
    y: host.y + dy,
    w: placement.widthMm,
    h: placement.heightMm,
  }
}
